<script setup lang="ts">
  import FormGroup from '@/components/FormGroup.vue';
  import { useMenuStore } from '@/composition/use-menu';
  import { useModal } from '@/composition/use-modal';
  import { useOrdering } from '@/composition/use-ordering';
  import { OrderType } from '@/enums/OrderEnums';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useOrdersStore } from '@/stores/orders.store';
  import { computed, inject, onBeforeMount, ref, toRef, watch } from 'vue';
  import ModalChooseAddress from '../../ModalChooseAddress.vue';
  import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';
  import ModalsCustomers from '@/components/modals/customer/ModalsCustomers.vue';

  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const modals = useModal();
  const props = defineProps<{
    startingMode?: 'edit' | 'view';
  }>();

  const mode = ref<'edit' | 'view'>(props.startingMode ?? 'edit');

  const tabs = computed(() => {
    const t = [
      {
        id: OrderType.Seat,
        label: 'A la mesa',
        icon: 'mdi-chair-rolling',
        disabled: !menuStore.availableOrderTypes?.some?.(
          (type) => type.type === OrderType.Seat
        ),
      },
      {
        id: OrderType.Away,
        label: 'Take away',
        icon: 'mdi-shopping',
        disabled: !menuStore.availableOrderTypes?.some?.(
          (type) => type.type === OrderType.Away
        ),
      },
      {
        id: OrderType.Delivery,
        label: 'Delivery',
        icon: 'mdi-truck-delivery',
        disabled: !menuStore.availableOrderTypes?.some?.(
          (type) => type.type === OrderType.Delivery
        ),
      },
    ];

    return t;
  });

  const orderHelper = inject<ReturnType<typeof useOrdering>>('orderHelper')!;
  const selectedTab = computed(() =>
    tabs.value.find((tab) => tab.id === menuStore.selectedOrderType)
  );

  const rawPhone = ref(orderHelper.customerData.value.phone ?? '');
  function phoneChanged(
    number: string,
    data: { number: { e164: string }; valid: boolean }
  ) {
    orderHelper.customerData.value.phone = data.number.e164;
  }

  watch(orderHelper.selectedCustomer, (customer) => {
    rawPhone.value = customer?.tel ?? '';
  });

  function blur(...args: any[]) {
    const input = args[0].target;
    input.blur();
  }

  const customerDataFilled = computed(() => {
    const customerData = orderHelper.customerData.value;

    switch (selectedTab.value.id) {
      case OrderType.Seat:
        return !!customerData.name && !!orderHelper.table.value;
      case OrderType.Away:
        return !!customerData.name && !!customerData.phone;
      case OrderType.Delivery:
        return (
          !!customerData.name &&
          !!customerData.phone &&
          !!orderHelper.deliveryInfo.value.address
        );
      default:
        return false;
    }
  });

  watch(
    () => orderHelper.currentCartTotalItems.value,
    (newValue) => {
      if (customerDataFilled.value) {
        mode.value = 'view';
      }
    }
  );

  onBeforeMount(() => {
    if (!props.startingMode && orderHelper.editOrder.value) {
      mode.value = 'view';
    }
  });

  function openAddressModal() {
    modals.show(
      // @ts-ignore
      ModalChooseAddress,
      {
        deliveryInfo: orderHelper.deliveryInfo.value,
      },
      {
        name: 'choose-address',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        save: (place: IDeliveryInfo) => {
          orderHelper.deliveryInfo.value = place;
          modals.hide('choose-address');
        },
      }
    );
  }

  function openCustomersModal() {
    modals.show(
      // @ts-ignore
      ModalsCustomers,
      {},
      {
        name: 'customers',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        chooseCustomer: (customer) => {
          orderHelper.selectedCustomer.value = customer;
          //rawPhone.value = customer.tel ?? '';
          modals.hide('customers');
        },
        close: () => modals.hide('customers'),
      }
    );
  }

  const orderTypeToCFOrderType = {
    [OrderType.Seat]: 's',
    [OrderType.Away]: 't',
    [OrderType.Delivery]: 'd',
  };
  const visibleCustomFields = computed(() => {
    const cF = restaurantStore.customFields;
    if (!cF) return cF;

    return cF.filter((cF) =>
      cF.orderTypes!.includes(
        orderTypeToCFOrderType[menuStore.selectedOrderType!]
      )
    );
  });

  const customFieldValues = toRef(orderHelper, 'customFieldValues');

  const activeTables = computed(() =>
    restaurantStore.tables?.filter?.((t) => t.enabled)
  );

  // Covers (cubiertos): only offered when this order opens the table
  // (no other pending seat order on it). Informative only.
  const tableIsIdle = computed(() => {
    const tableId = orderHelper.table.value?.id;
    if (!tableId) return false;
    const pendingSeat = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];
    return !pendingSeat.some(
      (o) => o.tableId === tableId && o.id !== orderHelper.editOrder.value?.id
    );
  });

  const showCoversInput = computed(
    () => selectedTab.value?.id === OrderType.Seat && tableIsIdle.value
  );

  // Create mode: clear the value whenever the input hides (e.g. switching
  // from an idle to an occupied table) so a stale covers never travels in the
  // payload. Edit mode must keep the loaded value: the update payload sends
  // covers explicitly (null clears), so nulling it here would wipe the stored
  // value whenever the input hides.
  watch(showCoversInput, (visible) => {
    if (!visible && !orderHelper.editOrder.value) {
      orderHelper.covers.value = null;
    }
  });

  const coversInput = computed({
    get: () =>
      orderHelper.covers.value != null ? String(orderHelper.covers.value) : '',
    set: (value: string) => {
      const parsed = Number.parseInt(value, 10);
      orderHelper.covers.value =
        Number.isFinite(parsed) && parsed >= 1 ? Math.min(200, parsed) : null;
    },
  });

  const fullAddress = computed(() => {
    if (!orderHelper.deliveryInfo.value.address) return null;

    let address = orderHelper.deliveryInfo.value.address;

    if (orderHelper.deliveryInfo.value.address2) {
      address += ', ' + orderHelper.deliveryInfo.value.address2;
    }

    if (orderHelper.deliveryInfo.value.addressComment) {
      address += ` (${orderHelper.deliveryInfo.value.addressComment})`;
    }

    return address;
  });
</script>

<template>
  <div class="order-info">
    <template v-if="mode === 'edit'">
      <!-- <div class="tabs">
        <v-btn
          v-for="(tab, index) in tabs"
          :key="tab.id"
          @click="selectedTabIndex = index"
          class="tab"
          :class="{ active: selectedTabIndex === index }"
          small
          :color="selectedTabIndex === index ? 'primary' : undefined"
        >
          <v-icon class="pr-1" small>{{ tab.icon }}</v-icon>
          <span>{{ tab.label }}</span>
        </v-btn>
      </div> -->
      <div class="info-header">
        <div class="type-select-container">
          <v-select
            :value="menuStore.selectedOrderType"
            @input="menuStore.setSelectedOrderType"
            :items="tabs"
            item-text="label"
            item-value="id"
            solo
            background-color="primary"
            dense
            hide-details
            class="type-select"
            dark
            flat
            height="2"
            :prepend-inner-icon="selectedTab.icon"
          />
        </div>
      </div>
      <div class="customer-info">
        <template v-if="selectedTab.id === OrderType.Seat">
          <div class="input-row">
            <v-icon size="16" color="grey">mdi-table-chair</v-icon>
            <v-select
              v-model="orderHelper.table.value"
              :items="activeTables"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              return-object
              placeholder="Seleccione una mesa"
              class="input"
            />
          </div>
          <div v-if="showCoversInput" class="input-row">
            <v-icon size="16" color="grey">mdi-account-group</v-icon>
            <v-text-field
              v-model="coversInput"
              type="number"
              min="1"
              max="200"
              outlined
              dense
              hide-details
              placeholder="Cubiertos (opcional)"
              @keydown.enter="blur"
              class="input"
            />
          </div>
        </template>
        <!-- Customer selector -->
        <div class="input-row">
          <v-icon size="16" color="grey">mdi-account</v-icon>
          <v-text-field
            v-model="orderHelper.customerData.value.name"
            outlined
            dense
            hide-details
            placeholder="Nombre del cliente"
            @keydown.enter="blur"
            class="input"
            :disabled="!!orderHelper.selectedCustomer.value"
          />
          <v-btn
            v-if="!orderHelper.selectedCustomer.value"
            @click="openCustomersModal"
            color="primary"
            small
            icon
            depressed
            outlined
          >
            <v-icon size="16">mdi-account-search</v-icon>
          </v-btn>
          <v-btn
            v-else
            color="primary"
            icon
            small
            outlined
            @click="orderHelper.selectedCustomer.value = null"
          >
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </div>
        <template v-if="selectedTab.id !== OrderType.Seat">
          <div class="input-row">
            <v-icon size="16" color="grey">mdi-phone</v-icon>
            <vue-tel-input-vuetify
              class="mt-0 input"
              label="WhatsApp"
              @input="phoneChanged"
              v-model="rawPhone"
              placeholder="Ingrese el número de teléfono"
              persistent-hint
              hint="Con número de area"
              :default-country="restaurantStore.restaurant?.country"
            />
          </div>
        </template>
        <template v-if="selectedTab.id === OrderType.Delivery">
          <div class="input-row">
            <v-icon size="16" color="grey">mdi-map-marker</v-icon>
            <v-btn
              v-if="!orderHelper.deliveryInfo.value"
              @click="openAddressModal"
              color="primary"
              small
              depressed
              rounded
              outlined
            >
              <span class="input">Seleccionar dirección</span>
            </v-btn>
            <template v-else>
              <span class="input">{{ fullAddress }}</span>
              <v-btn @click="openAddressModal" color="primary" small icon text>
                <v-icon size="16">mdi-pencil</v-icon>
              </v-btn>
            </template>
          </div>
        </template>
        <template
          v-if="restaurantStore.customFields?.length && customFieldValues"
        >
          <div
            v-for="customField in visibleCustomFields"
            :key="customField.id"
            class="input-row"
          >
            <v-icon size="16" color="grey">mdi-text</v-icon>
            <v-text-field
              :value="customFieldValues[customField.id]"
              @input="
                ($event) => {
                  customFieldValues[customField.id] = $event;
                }
              "
              outlined
              dense
              hide-details
              :label="customField.title"
              :placeholder="`Ingrese ${customField.title}`"
              @keydown.enter="blur"
              class="input"
            />
          </div>
        </template>
        <!-- Order comment -->
        <div class="input-row">
          <v-icon size="16" color="grey">mdi-comment</v-icon>
          <v-textarea
            v-model="orderHelper.cart.value.userComment"
            outlined
            dense
            hide-details
            placeholder="Indicaciones del pedido"
            @keydown.enter="blur"
            class="input"
            :rows="2"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="view-mode">
        <div class="view-header">
          <v-chip
            :color="selectedTab.id === OrderType.Seat ? 'primary' : 'secondary'"
            x-small
            class="order-type-chip"
            outlined
          >
            <v-icon size="16" class="mr-1">{{ selectedTab.icon }}</v-icon>
            {{ selectedTab.label }}
          </v-chip>
          <v-btn
            @click="mode = 'edit'"
            class="edit-button"
            color="primary"
            small
            text
          >
            <v-icon left>mdi-pencil</v-icon>
            Editar
          </v-btn>
        </div>

        <div class="customer-info">
          <div class="info-section">
            <template v-if="selectedTab.id === OrderType.Seat">
              <div class="info-row">
                <v-icon size="16" color="grey">mdi-table-chair</v-icon>
                <span class="info-value">{{
                  orderHelper.table.value?.name
                }}</span>
              </div>
              <div v-if="orderHelper.covers.value != null" class="info-row">
                <v-icon size="16" color="grey">mdi-account-group</v-icon>
                <span class="info-value"
                  >{{ orderHelper.covers.value }} cubiertos</span
                >
              </div>
            </template>

            <template v-if="selectedTab.id === OrderType.Delivery">
              <div class="info-row">
                <v-icon size="16" color="grey">mdi-map-marker</v-icon>
                <span class="info-value">{{
                  orderHelper.deliveryInfo.value.address
                }}</span>
              </div>
              <div
                v-if="orderHelper.deliveryInfo.value.addressComment"
                class="info-line"
              >
                <v-icon small>mdi-map-marker-alert-outline</v-icon>
                <span>{{ orderHelper.deliveryInfo.value.addressComment }}</span>
              </div>
            </template>

            <div class="info-row">
              <v-icon size="16" color="grey">mdi-account</v-icon>
              <span class="info-value">{{
                orderHelper.customerData.value.name || 'Sin nombre'
              }}</span>
            </div>

            <template v-if="selectedTab.id !== OrderType.Seat">
              <div class="info-row">
                <v-icon size="16" color="grey">mdi-phone</v-icon>
                <span class="info-value">{{
                  orderHelper.customerData.value.phone
                }}</span>
              </div>
            </template>

            <template v-if="restaurantStore.customFields?.length">
              <div
                v-for="customField in visibleCustomFields"
                :key="customField.id"
                class="info-row"
              >
                <v-icon size="16" color="grey">mdi-text</v-icon>
                <span
                  class="info-value"
                  :class="{
                    light: !customFieldValues[customField.id],
                  }"
                  >{{
                    customFieldValues[customField.id] ||
                    `Sin ${customField.title}`
                  }}</span
                >
              </div>
            </template>

            <div class="info-row">
              <v-icon size="16" color="grey">mdi-comment</v-icon>
              <span class="info-value light">{{
                orderHelper.cart.value.userComment || 'Sin indicaciones'
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .order-info {
    @apply tw-rounded-lg tw-px-2 tw-py-1 tw-space-y-2;
    @apply tw-border tw-border-gray-200;

    .info-header {
      @apply tw-flex tw-justify-start;

      .type-select-container {
        @apply tw-w-[12rem];

        .type-select {
          @apply tw-rounded-lg;
          ::v-deep(.v-input__control) {
            min-height: 2rem;

            & > div {
              padding: 0 4px !important;
            }

            .v-input__icon {
              min-height: 18px;

              i {
                font-size: 18px;
              }
            }
          }
        }
      }
    }

    .customer-info {
      @apply tw-space-y-1;

      .input-row {
        @apply tw-flex tw-items-center tw-space-x-2 tw-py-0.5;

        .input {
          @apply tw-flex-1;
        }
      }
    }

    .view-mode {
      .view-header {
        @apply tw-flex tw-items-center tw-justify-between tw-px-1;

        .order-type-chip {
          @apply tw-text-xs tw-h-6;
        }

        .edit-button {
          @apply tw-px-0;
        }
      }

      .customer-info {
        .info-section {
          @apply tw-divide-y tw-divide-gray-100;
        }

        .info-row {
          @apply tw-flex tw-items-center tw-space-x-2 tw-px-1 tw-py-0.5 tw-text-sm;

          .v-icon {
            @apply tw-opacity-75;
          }

          .info-value {
            @apply tw-text-gray-700 tw-font-medium;

            &.light {
              @apply tw-text-gray-500 tw-font-normal;
            }
          }
        }
      }
    }
  }
</style>
